<template>
  <section class="benchmark-metrics">
    <page-sub-header title="Page Visits" class="mt-4" />
    <sme-card>
      <app-loading v-if="loading" />
      <template v-else>
        <sme-alert v-if="hasNoSector" level="info">
          Sector comparison not available - your company has not been assigned to a sector.
        </sme-alert>
        <template v-else-if="barMetrics.length">
          <p class="mb-3 text-muted">
            Comparing page visits against the <strong>{{ sector }}</strong> sector average. Percentages are based on
            <strong>{{ uniqueUsersFormatted }}</strong> unique visitors in the last complete month.
          </p>
          <benchmark-bar-metric
            v-for="metric in barMetrics"
            :key="metric.id"
            :label="metric.label"
            :value="metric.value"
            :average="metric.average"
            :max-value="metric.maxValue"
            :formatted-value="metric.formattedValue"
            :formatted-average="metric.formattedAverage"
            :delta-formatted="metric.deltaFormatted"
            :delta-color-class="metric.deltaColorClass"
            :value-label="metric.valueLabel"
          />
        </template>
        <p v-else class="my-2 text-center">No data to display.</p>
      </template>
    </sme-card>
  </section>
</template>

<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import ApiClient from '@/ApiClient';
import { getBankingInfo } from '@/Banking';
import AppLoading from '@/components/AppLoading.vue';
import PageSubHeader from '@/components/PageSubHeader.vue';
import SmeAlert from '@/components/atoms/SmeAlert.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';
import BenchmarkBarMetric from '@/pages/analyticsBenchmark/components/BenchmarkBarMetric.vue';
import { formatValue, calculateDelta } from '@/pages/analyticsBenchmark/benchmarkUtils';
import State from '@/state/State';
import { TIME_SERIES } from '@/utils/chart';

const PRODUCT_MAPPINGS = {
  track_users: 'Track',
  save_users: getBankingInfo().save.label,
  stream_users: 'Pay',
  hub_users: 'Hub',
};

const companyLabel = computed(() => State.state.company?.name || 'Your company');

const loading = ref(true);
const sector = ref(null);
const tableData = ref([]);
const hasNoSector = ref(false);
const uniqueUsersFormatted = ref('-');

const barMetrics = computed(() =>
  tableData.value.map(row => {
    const { formatted: deltaFormatted, colorClass: deltaColorClass } = calculateDelta(row.current, row.benchmark, {
      deltaFormat: 'percentage_points',
      polarity: 'higher_is_better',
      unit: 'percent',
    });

    return {
      id: row.product,
      label: row.product,
      value: row.current,
      average: row.benchmark,
      maxValue: 100,
      valueLabel: companyLabel.value,
      formattedValue: formatValue(row.current, 'percent'),
      formattedAverage: formatValue(row.benchmark, 'percent'),
      deltaFormatted,
      deltaColorClass,
    };
  }),
);

const fetchBenchmarkData = async () => {
  loading.value = true;

  try {
    const response = await ApiClient.getAppFeaturesMetrics(TIME_SERIES.MONTH_TO_MONTH, null, true);
    const { results, sector: sectorName } = response;

    if (!sectorName) {
      hasNoSector.value = true;
      loading.value = false;
      return;
    }

    sector.value = sectorName;

    if (!results?.length) {
      loading.value = false;
      return;
    }

    const uniqueUsersResult = results.find(result => result.id === 'unique_users');
    const uniqueUsers = uniqueUsersResult?.calculated?.last_complete || 0;
    uniqueUsersFormatted.value = uniqueUsers.toLocaleString('en-GB');

    const productIds = ['track_users', 'save_users', 'stream_users', 'hub_users'];
    const rows = [];

    productIds.forEach(productId => {
      const productResult = results.find(result => result.id === productId);
      if (productResult && PRODUCT_MAPPINGS[productId]) {
        const lastValue = productResult.calculated?.last_complete || 0;
        const productCurrent = uniqueUsers ? (lastValue / uniqueUsers) * 100 : 0;
        const productSectorAvg = productResult.sector_averages?.[productResult.sector_averages.length - 1] ?? null;

        rows.push({
          product: PRODUCT_MAPPINGS[productId],
          current: productCurrent,
          benchmark: productSectorAvg,
        });
      }
    });

    tableData.value = rows;
  } catch (error) {
    console.error('Failed to fetch benchmark data:', error);
  }

  loading.value = false;
};

onBeforeMount(() => fetchBenchmarkData());
</script>
